import { trackAnalyticsEvent } from 'analytics';
import { HTML5Configuration } from 'Configuration/accessors/HTML5ConfigAccessor';
import { ChromeAppMessage } from 'Environment/ChromeApp/types';
import { HTML5IframeBridge } from 'Environment/Html5/html5IframeBridge';
import { HTML5EventPayload } from 'Workspace/TelemetryEvents/html5Event/html5CASReporter';

const INIT_EVENT = 'workspace/cvad/html5/initialised' as const;
const INIT_EVENT_ACK = 'workspace/cvad/html5/initialisedAck' as const;
// "init" event needs cleanup later as new "initialised" event will be used
const INITIALIZE_EVENT = 'workspace/cvad/html5launch/icadownload/init' as const;
const SUCCESS_EVENT = 'workspace/cvad/html5launch/icadownload/success' as const;
const ERROR_EVENT = 'workspace/cvad/html5launch/icadownload/error' as const;
const RETRY_EVENT = 'workspace/cvad/html5launch/icadownload/retry' as const;
const INIT_MESSAGE_TIMEOUT_MS = 10000;

interface FetchIcaLaunchConfig {
	launchId: number;
	resourceId: string;
	correlationId: string;
}
export interface HTML5ClientCapabilities {
	inspectConnection?: boolean;
	sessionReconnect?: boolean;
}

export interface HTML5ClientPlatform {
	appversion?: string;
	buildVersion?: string;
	osversion?: string;
	deviceId?: string;
}

interface PlatformInfo {
	wsuiVersion: string;
	buildVersion: string;
}
export interface WSUICapabilities {
	apiversion: string;
	platform: PlatformInfo;
	capabilities: HTML5Configuration;
}

interface ResourceLaunchData {
	launchId: number;
	resourceId: string;
}

type BaseEvent = {
	type: string;
	payload: unknown;
};

type SuccessEvent = {
	type: typeof SUCCESS_EVENT;
	payload: {
		icaData: object;
	};
};

type ErrorEvent = {
	type: typeof ERROR_EVENT;
	payload: {
		errorMessage: string;
	};
};

type RetryEvent = {
	type: typeof RETRY_EVENT;
	payload: null;
};

type InitAckEvent = {
	header: {
		type: typeof INIT_EVENT_ACK;
	};
	payload: {
		correlationId: string;
		apiversion: string;
		capabilities: HTML5Configuration;
		platform: PlatformInfo;
		[key: string]: any;
	};
};

type InitEvent = {
	header: {
		type: typeof INIT_EVENT;
	};
	payload: {
		correlationId: string;
		capabilities?: HTML5ClientCapabilities;
		platform?: HTML5ClientPlatform;
		[key: string]: any;
	};
};

type InitializeEvent = {
	type: typeof INITIALIZE_EVENT;
	payload: {
		version: number;
	};
};

type SentEvents = SuccessEvent | ErrorEvent | RetryEvent | InitAckEvent;

export class HTML5Client {
	private usePostMessage = false;
	private errorId: string;
	private initMessageListener: ((event: MessageEvent) => void) | null = null;
	private initTimeoutId: NodeJS.Timeout | null = null;
	private html5Capabilities: HTML5ClientCapabilities | null = null;
	private html5Platform: HTML5ClientPlatform | null = null;

	public get capabilities(): HTML5ClientCapabilities | null {
		return this.html5Capabilities;
	}

	public get platform(): HTML5ClientPlatform | null {
		return this.html5Platform;
	}

	public constructor(private readonly targetWindow: Window & Record<string, any>) {}

	public startListening(wsuiCapabilities: WSUICapabilities) {
		if (!wsuiCapabilities.capabilities.supportsHTML5InitialisedEvent) {
			window.addEventListener('message', this.onMessage);
			return Promise.resolve(null);
		} else {
			return this.setupInitMessageHandling(wsuiCapabilities);
		}
	}

	public clearInitTimeout() {
		// Clear timeout if it exists
		if (this.initTimeoutId) {
			clearTimeout(this.initTimeoutId);
			this.initTimeoutId = null;
		}
	}

	public sendFetchIcaSuccess(icaData: object, launchConfig: FetchIcaLaunchConfig) {
		const { launchId, correlationId, resourceId } = launchConfig;
		const fetchIcaParams = {
			icaData,
			launchId,
			correlationId,
			resourceId,
		};
		HTML5IframeBridge.getInstance()?.sendFetchIcaResponse(fetchIcaParams);
	}

	public sendSuccess(icaData: object, resourceLaunchData: ResourceLaunchData) {
		if (this.usePostMessage) {
			this.sendEvent({
				type: SUCCESS_EVENT,
				payload: {
					icaData,
					resourceId: resourceLaunchData.resourceId,
				},
			});
		} else {
			window.html5LaunchData ??= {};
			window.html5LaunchData[resourceLaunchData.launchId] = icaData;
			if (this.targetWindow) {
				this.targetWindow.icaDwldComplete = true;
			}
		}
	}

	/**
	 * Errors can be triggered in a few different places in the launch flow, but an explicit
	 * error id should be captured as soon as it's known for maximum accuracy -- the error
	 * may be wrapped up and thrown later. The error id is passed to the html5 client so
	 * it can be surfaced in collected logs.
	 */
	public setErrorId(errorId: string) {
		this.errorId = errorId;
	}

	private getErrorMessage(error: unknown): string {
		if (this.errorId) {
			return this.errorId;
		} else if (error instanceof Error) {
			return `${error.name}: ${error.message}`;
		} else {
			return 'ICADownloadError';
		}
	}

	public sendError(error: unknown) {
		if (this.usePostMessage) {
			const errorMessage = this.getErrorMessage(error);
			this.sendEvent({
				type: ERROR_EVENT,
				payload: { errorMessage },
			});
		} else {
			this.targetWindow?.close();
		}
	}

	/**
	 * With a retry event we can notify the HTML5 client that the cloud broker is online
	 * and responding, but that the VDA needs more time to spin up. The HTML5 client
	 * should then cancel its own launch flow timeout and delegate to the broker's
	 * timeout, which is configured by the customer's admin. This does not _completely_
	 * remove timeouts, as WSP still enforces an internal 60-second timeout for each
	 * outgoing call. After a retry, it's assumed an eventual error or success will
	 * follow
	 */
	public sendRetry() {
		if (this.usePostMessage) {
			this.sendEvent({
				type: RETRY_EVENT,
				payload: null,
			});
		}
	}

	private onMessage = (message: unknown) => {
		if (this.isInitializeEventFromLaunchWindow(message)) {
			this.usePostMessage = true;
			trackAnalyticsEvent(
				HTML5EventPayload.postMessageReceived({
					messageType: INITIALIZE_EVENT,
				})
			);
		}
	};

	private isInitializeEventFromLaunchWindow(
		event: unknown
	): event is MessageEvent<InitializeEvent> {
		if (event instanceof MessageEvent && event.origin === location.origin) {
			const { data } = event;
			return (
				data?.type === INITIALIZE_EVENT && typeof data?.payload?.version === 'number'
			);
		}

		return false;
	}

	public sendEvent<E extends (BaseEvent & SentEvents) | ChromeAppMessage>(event: E) {
		this.targetWindow.postMessage(event, location.origin);
		const messageType = 'header' in event ? event.header.type : event.type;
		const analyticsPayload: { messageType: string; correlationId?: string } = {
			messageType,
		};

		if (event.payload && 'correlationId' in event.payload) {
			analyticsPayload.correlationId = event.payload.correlationId;
		}
		trackAnalyticsEvent(HTML5EventPayload.postMessageSent(analyticsPayload));
	}

	/**
	 * Validates whether an event is part of the HTML5 initialization handshake.
	 * This functionality is controlled by the feature flag: WSUI11394EnableHTML5InitialisedEvent
	 * Both GACS v2 and session reconnect features require this event handling to be enabled.
	 */
	private isInitializeEvent(event: unknown): event is MessageEvent<InitEvent> {
		if (event instanceof MessageEvent && event.origin === location.origin) {
			const { data } = event;
			if (
				data?.header?.type === INIT_EVENT &&
				typeof data?.payload?.correlationId === 'string'
			) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Sets up init message handling with timeout
	 * Returns a promise that resolves when init message is received or times out
	 */
	public setupInitMessageHandling(wsuiCapabilities: WSUICapabilities): Promise<any> {
		return new Promise<any>(resolve => {
			this.initMessageListener = (event: MessageEvent) => {
				// Early return if not from the expected target window
				if (event.source !== this.targetWindow) {
					return;
				}
				const messageType = event.data?.header?.type || event.data?.type;
				if (!messageType) {
					return;
				}
				trackAnalyticsEvent(
					HTML5EventPayload.postMessageReceived({
						messageType,
						correlationId: event.data?.payload?.correlationId,
					})
				);

				// Check if this is an InitializeEventV2
				if (!this.isInitializeEvent(event)) {
					return;
				}
				this.html5Capabilities = event.data.payload?.capabilities || null;
				this.html5Platform = event.data.payload?.platform || null;

				const ackEvent: InitAckEvent = {
					header: {
						type: INIT_EVENT_ACK,
					},
					payload: {
						correlationId: event.data.payload?.correlationId,
						apiversion: wsuiCapabilities?.apiversion,
						capabilities: wsuiCapabilities?.capabilities,
						platform: {
							wsuiVersion: wsuiCapabilities?.platform?.wsuiVersion,
							buildVersion: wsuiCapabilities?.platform?.buildVersion,
						},
					},
				};

				this.sendEvent(ackEvent); // Acknowledge receipt with WSUI capabilities
				this.usePostMessage = true;
				resolve(event.data);
			};

			// Start listening for init message immediately
			window.addEventListener('message', this.initMessageListener);

			// Set up timeout - continue launch even if init message doesn't arrive
			this.initTimeoutId = setTimeout(() => {
				resolve(null); // Resolve with null instead of rejecting
			}, INIT_MESSAGE_TIMEOUT_MS); // 10 second timeout
		});
	}
}
